import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface PlayerState {
  // Player state
  videoId: string | null;
  position: number;
  isPlaying: boolean;
  duration: number;
  volume: number;

  // Player instance
  player: YT.Player | null;
  isReady: boolean;

  // Actions
  setVideoId: (videoId: string | null) => void;
  setPosition: (position: number) => void;
  setIsPlaying: (isPlaying: boolean) => void;
  setDuration: (duration: number) => void;
  setVolume: (volume: number) => void;
  setPlayer: (player: YT.Player | null) => void;
  setReady: (ready: boolean) => void;
  reset: () => void;
}

const initialState = {
  videoId: null,
  position: 0,
  isPlaying: false,
  duration: 0,
  volume: 50,
  player: null,
  isReady: false,
};

export const usePlayerStore = create<PlayerState>()(
  persist(
    (set) => ({
      ...initialState,

      setVideoId: (videoId) => set({ videoId }),

      setPosition: (position) => set({ position }),

      setIsPlaying: (isPlaying) => set({ isPlaying }),

      setDuration: (duration) => set({ duration }),

      setVolume: (volume) => {
    set({ volume });
    console.log(`[Volume] Setting volume to ${volume}`);

    // Update player volume asynchronously to avoid race conditions
    const updatePlayerVolume = (attemptNumber: number) => {
      const state = usePlayerStore.getState();
      console.log(`[Volume] Attempt ${attemptNumber}: player=${!!state.player}, ready=${state.isReady}, videoId=${state.videoId}`);

      if (!state.player || !state.isReady) {
        console.log(`[Volume] Attempt ${attemptNumber}: Player not ready`);
        return false;
      }

      // Don't try to set volume if no video is loaded yet
      if (!state.videoId) {
        console.log(`[Volume] Attempt ${attemptNumber}: No video loaded`);
        return false;
      }

      try {
        // Check if setVolume method exists
        if (typeof state.player.setVolume !== 'function') {
          console.log(`[Volume] Attempt ${attemptNumber}: setVolume method not available`);
          return false;
        }

        // Try to get player state - this can also throw if iframe not ready
        let playerState;
        try {
          playerState = state.player.getPlayerState();
        } catch (stateError) {
          // Can't get state, player iframe not ready
          console.log(`[Volume] Attempt ${attemptNumber}: Cannot get player state`, stateError);
          return false;
        }

        console.log(`[Volume] Attempt ${attemptNumber}: Player state = ${playerState}`);

        // Only set volume when player is in a stable state
        // -1 = unstarted, 0 = ended, 1 = playing, 2 = paused, 3 = buffering, 5 = cued
        // Avoid setting during buffering (3) or unstarted (-1)
        if (playerState === 3 || playerState === -1) {
          console.log(`[Volume] Attempt ${attemptNumber}: Player in unstable state (${playerState})`);
          return false;
        }

        // Try to set the volume
        console.log(`[Volume] Attempt ${attemptNumber}: Calling setVolume(${volume})`);
        state.player.setVolume(volume);
        console.log(`[Volume] Attempt ${attemptNumber}: SUCCESS!`);
        return true; // Success
      } catch (error) {
        // Silently ignore - player iframe might not be fully ready yet
        console.error(`[Volume] Attempt ${attemptNumber}: ERROR`, error);
        return false;
      }
    };

    // Try with increasing delays, stop if successful
    const tryWithDelay = (delay: number, maxRetries: number, retryCount = 0) => {
      setTimeout(() => {
        const success = updatePlayerVolume(retryCount + 1);
        if (!success && retryCount < maxRetries) {
          // If failed and we have retries left, try again with next delay
          const nextDelay = delay === 0 ? 100 : delay * 2;
          console.log(`[Volume] Retry scheduled in ${nextDelay}ms`);
          tryWithDelay(nextDelay, maxRetries, retryCount + 1);
        } else if (success) {
          console.log(`[Volume] Volume set successfully after ${retryCount + 1} attempts`);
        } else {
          console.log(`[Volume] Failed to set volume after ${maxRetries + 1} attempts`);
        }
      }, delay);
    };

    // Start with immediate attempt, then retry up to 5 times with exponential backoff
    // 0ms, 100ms, 200ms, 400ms, 800ms
    tryWithDelay(0, 5);
  },

      setPlayer: (player) => set({ player }),

      setReady: (ready) => set({ isReady: ready }),

      reset: () => set(initialState),
    }),
    {
      name: 'yt-music-sync-player',
      partialize: (state) => ({
        videoId: state.videoId,
        position: state.position,
        isPlaying: state.isPlaying,
        duration: state.duration,
        volume: state.volume,
        // Don't persist player instance or isReady
      }),
    }
  )
);
